import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from './AuthContext';

const TimeTrackingContext = createContext();

export const useTimeTracking = () => {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
  }
  return context;
};

export const TimeTrackingProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [currentJob, setCurrentJob] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [shiftStarted, setShiftStarted] = useState(false);
  const [breakType, setBreakType] = useState(null);
  const [pauseReason, setPauseReason] = useState(null);

  // Break time definitions
  const breakSchedule = {
    lunch: { start: '13:00', end: '14:30', duration: 45 }, // 45 min within 1.5 hours
    snacks: { start: '18:00', end: '18:30', duration: 15 }, // 15 min within 30 min
    dinner: { start: '20:30', end: '21:30', duration: 45 } // 45 min within 1 hour
  };

  const pauseReasons = [
    'No jobs',
    'Call with Customers',
    'Meeting',
    'Training',
    'Rest Room break'
  ];

  const logTimeEvent = async (eventData) => {
    try {
      await api.post('/time/event', {
        ...eventData,
        user_id: user.id,
        ts: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to log time event:', error);
      throw error;
    }
  };

  const startShift = async () => {
    try {
      await logTimeEvent({
        event: 'shift_start',
        job_id: null
      });
      setShiftStarted(true);
    } catch (error) {
      throw new Error('Failed to start shift');
    }
  };

  const endShift = async () => {
    try {
      await logTimeEvent({
        event: 'shift_end',
        job_id: null
      });
      setShiftStarted(false);
      setIsWorking(false);
      setCurrentJob(null);
    } catch (error) {
      throw new Error('Failed to end shift');
    }
  };

  const startJob = async (jobId) => {
    try {
      await logTimeEvent({
        event: 'start',
        job_id: jobId
      });
      setCurrentJob(jobId);
      setIsWorking(true);
    } catch (error) {
      throw new Error('Failed to start job');
    }
  };

  const pauseJob = async (reason) => {
    try {
      await logTimeEvent({
        event: 'pause',
        job_id: currentJob,
        notes: reason
      });
      setIsWorking(false);
      setPauseReason(reason);
    } catch (error) {
      throw new Error('Failed to pause job');
    }
  };

  const resumeJob = async () => {
    try {
      await logTimeEvent({
        event: 'resume',
        job_id: currentJob
      });
      setIsWorking(true);
      setPauseReason(null);
    } catch (error) {
      throw new Error('Failed to resume job');
    }
  };

  const endJob = async () => {
    try {
      await logTimeEvent({
        event: 'end',
        job_id: currentJob
      });
      setCurrentJob(null);
      setIsWorking(false);
    } catch (error) {
      throw new Error('Failed to end job');
    }
  };

  const startBreak = async (type) => {
    try {
      await logTimeEvent({
        event: 'break_start',
        job_id: currentJob,
        break_type: type
      });
      setIsOnBreak(true);
      setBreakType(type);
    } catch (error) {
      throw new Error('Failed to start break');
    }
  };

  const endBreak = async () => {
    try {
      await logTimeEvent({
        event: 'break_end',
        job_id: currentJob,
        break_type: breakType
      });
      setIsOnBreak(false);
      setBreakType(null);
    } catch (error) {
      throw new Error('Failed to end break');
    }
  };

  const value = {
    currentJob,
    isWorking,
    isOnBreak,
    shiftStarted,
    breakType,
    pauseReason,
    breakSchedule,
    pauseReasons,
    startShift,
    endShift,
    startJob,
    pauseJob,
    resumeJob,
    endJob,
    startBreak,
    endBreak
  };

  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
};